/* RESPONSIBLE TEAM: team-inbox-experience */
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { Resource } from 'ember-resources/core';
import { type Named } from 'ember-resources/core/types';
import { registerDestructor } from '@ember/destroyable';
import type ApplicationInstance from '@ember/application/instance';
import type {
  ConversationPdfDownloadReadyEvent,
  ConversationPdfExportFailedEvent,
} from 'embercom/services/nexus';
import safeWindowOpen from 'embercom/lib/safe-window-open';

const PDF_EXPORT_TIMEOUT_MS = 30000;

type Args = {
  conversationId: number;
};

/* eslint-disable @intercom/intercom/no-component-inheritance */
export default class PdfExportResource extends Resource<Named<Args>> {
  @tracked errorType?: 'timeout' | 'failure';

  private timeoutId?: ReturnType<typeof setTimeout>;
  private conversationId?: number;
  private exportRequestId?: string;
  private pendingEvent?: ConversationPdfDownloadReadyEvent;

  constructor(owner: ApplicationInstance) {
    super(owner);

    registerDestructor(this, () => {
      this.clearTimeout();
    });
  }

  modify(_: unknown[], args: Args) {
    this.conversationId = args.conversationId;
  }

  startExport(requestId: string): void {
    this.clearTimeout();
    this.errorType = undefined;

    this.exportRequestId = requestId;

    // Process buffered event if it arrived before request_id was stored (race condition)
    if (this.pendingEvent) {
      let event = this.pendingEvent;
      this.pendingEvent = undefined;
      this.handlePdfDownloadReady(event);
      return;
    }

    this.timeoutId = setTimeout(() => {
      this.errorType = 'timeout';
    }, PDF_EXPORT_TIMEOUT_MS);
  }

  @action
  handlePdfDownloadReady(event: ConversationPdfDownloadReadyEvent): void {
    // Buffer event if it arrived before request_id was stored (race condition)
    if (!this.exportRequestId && event.eventData.conversation_id === this.conversationId) {
      this.pendingEvent = event;
      return;
    }

    if (
      event.eventData.conversation_id === this.conversationId &&
      event.eventData.request_id === this.exportRequestId
    ) {
      this.clearTimeout();
      safeWindowOpen(event.eventData.download_url, '_blank');
      // Clear request_id to prevent duplicate processing and ensure multi-tab isolation
      this.exportRequestId = undefined;
    }
  }

  @action
  handlePdfExportFailed(event: ConversationPdfExportFailedEvent): void {
    if (
      event.eventData.conversation_id === this.conversationId &&
      event.eventData.request_id === this.exportRequestId
    ) {
      this.clearTimeout();
      this.errorType = 'failure';
      // Clear request_id after handling failure to prevent duplicate processing
      this.exportRequestId = undefined;
    }
  }

  @action
  closeErrorModal(): void {
    this.errorType = undefined;
  }

  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }
}
